// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { createLogger } from 'redux-logger';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import { selectPricingInfos } from '@gandi/billing';
import { selectCartIsWorking, type DecoratedProduct } from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Base, Block, Button, Placeholder, Text } from '@gandi/ui';

import Counter, {
  CounterStateContext,
} from 'src/Overview/components/Products/components/components/Rows/Counter';

import { useAppSelector } from 'src/hooks';
import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import Price from 'src/shared/Price';

import useMailboxLabel from '../../MailboxCreate/hooks/useMailboxLabel';

import { AutoRenewRow } from '../../Overview/components/Products/components/components/Rows/AutoRenewRow';
import { useOnAutoRenewChange } from '../../Overview/components/Products/components/modules/mailbox';
import { useDurationToLabel } from '../modules/hooks/useDurationToLabel';
import { useRemoveCartMailboxes } from '../modules/hooks/useRemoveCartMailboxes';
import { useUpsertCartMailboxes } from '../modules/hooks/useUpsertCartMailboxes';

export type Props = {
  product: DecoratedProduct,
};

const logger = createLogger('Mailbox SA item');

export function MailboxStandaloneItem({ product }: Props): React.MixedElement {
  const translator = useTranslator();
  const { __ } = translator;
  const dispatch = useDispatch();

  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();

  // $FlowFixMe[incompatible-call]
  const { currency } = useAppSelector(selectPricingInfos);
  const hasTaxes = product.computedTotalPrices.rate !== 0;
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);
  const amount = displayTaxes
    ? product.computedTotalPrices.afterTaxes
    : product.computedTotalPrices.beforeTaxes;

  const isCartWorking = useAppSelector(selectCartIsWorking);

  const [quantity, setQuantity] = React.useState(product.quantity);

  const { durationToBillingCycle, durationToPeriodicity } =
    useDurationToLabel();

  const { handleUpsertCartMailboxes } = useUpsertCartMailboxes();
  const { handleRemoveCartMailboxes } = useRemoveCartMailboxes();

  const getMailboxLabel = useMailboxLabel();

  const counterState = React.useContext(CounterStateContext);

  async function handleChange(value: number) {
    setQuantity(value);

    try {
      await handleUpsertCartMailboxes(
        {
          ...product.payload,
          quantity: value,
        },
        product.id,
      );
    } catch (err) {
      dispatch(
        notifyDanger({
          message:
            err.response?.data.message ||
            __('We are unable to add mailboxes to your cart.'),
        }),
      );
    }
  }

  async function handleDelete() {
    try {
      await handleRemoveCartMailboxes(product.ids);
    } catch (err) {
      dispatch(
        logger.warn({ err }, 'Unable to remove mailboxes from the cart.'),
      );

      dispatch(
        notifyDanger({
          message:
            err.response?.data.message ||
            __('We are unable to remove mailboxes from your cart.'),
        }),
      );
    }
  }

  const onAutoRenewChange = useOnAutoRenewChange(product);

  // Force syncing `quantity` with `product.quantity` when `product.quantity`
  // change
  React.useEffect(() => {
    if (
      Number.isFinite(product.payload.quantity) &&
      product.payload.quantity !== quantity
    ) {
      // $FlowIssue[incompatible-call]
      setQuantity(product.payload.quantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.payload.quantity]);

  return (
    <Block marginBottom="small">
      <Base
        flex
        flexJustifyBetween
        flexWrap
        gap="small"
        marginBottom="small"
        size="base"
        tagName="h3"
      >
        <Base flex flexCol gap="xsmall" transform="capitalize">
          {getMailboxLabel(
            // $FlowFixMe[incompatible-call] config_name is always defined
            product.payload.config_name,
            product.payload.config_name === 'standard'
              ? __('10 GB')
              : __('50 GB'),
          )}
          <Text color="light" weight="regular">
            {durationToBillingCycle(product.payload.duration_month)}
          </Text>
        </Base>
        <Base flex flexAlignItemsCenter flexJustifyEnd flexWrap gap="small">
          <Counter
            id={`count-${product.id}`}
            count={product.payload.quantity || 1}
            onChange={handleChange}
            callbackDependencies={[product.payload.autorenew]}
            // we only need cart activity here, we want to allow changes while debouncing
            isLoading={isCartWorking}
          />
          <Button
            disabled={isCartWorking || counterState?.hasActivity}
            intent="danger"
            kind="outline"
            onClick={handleDelete}
            type="button"
          >
            {__('Remove')}
          </Button>
        </Base>
      </Base>
      {!amount ? (
        <Base
          flex
          flexAlignItemsStart
          flexCol
          gap="xsmall"
          marginBottom="small"
        >
          <Placeholder chars={5} />
          <Placeholder chars={10} />
        </Base>
      ) : (
        <Base marginBottom="small">
          <Price
            currency={currency}
            price={amount}
            priceInfo={` ${durationToPeriodicity(
              product.payload.duration_month,
            )}`}
            showExcludeVatMention={hasTaxes && !displayTaxes}
          />
        </Base>
      )}

      {!FF_CART_OVERVIEW_NDS && (
        <AutoRenewRow
          productId={product.id}
          checked={!!product.payload?.autorenew}
          onChange={onAutoRenewChange}
        />
      )}
    </Block>
  );
}
